import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import { Department, isDepartmentActive } from 'types/department';
import AxiosUtils from 'utils/axios-utils';

export interface DepartmentOption {
  value: string;
  label: string;
}

/**
 * Danh sách Bộ phận cho ô chọn ở popup Sửa / Thêm thành viên.
 *
 * - Backend có danh mục phòng ban (trang Quản trị → Nhân sự → Bộ phận, API `GET /departments`):
 *   dùng các bộ phận đang bật, giữ thứ tự server trả về (sortOrder rồi tên).
 * - Backend chưa có API đó (404) hoặc lỗi: dùng `fallback` (danh sách cố định theo TYPE_DEPARTMENT).
 *
 * Không dùng `GET /attendance/departments` (dạng gọn công khai) vì ở một số bản backend danh sách
 * đó cố ý bỏ "QL Vận Hành" — chỉ Admin được đặt, nên trang Quản trị phải luôn thấy.
 * Giá trị đang lưu của thành viên mà không còn trong danh sách (bộ phận đã tắt) vẫn được giữ
 * thành một lựa chọn để không bị mất khi mở popup.
 */
export function useDepartmentOptions(fallback: DepartmentOption[], current?: string | null) {
  const [options, setOptions] = useState<DepartmentOption[]>(fallback);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await AxiosUtils.get<undefined, { data: Department[] }>(API.departments);
        if (!Array.isArray(res?.data)) throw new Error('Invalid departments response');
        const fromApi = res.data
          .filter(isDepartmentActive)
          .map((d) => ({ value: d.code, label: d.name }));
        if (!cancelled && fromApi.length > 0) setOptions(fromApi);
      } catch {
        // Giữ danh sách cố định.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fallback]);

  const withCurrent =
    current && !options.some((o) => o.value === current)
      ? [...options, { value: current, label: fallback.find((o) => o.value === current)?.label ?? current }]
      : options;

  return { options: withCurrent, loading };
}
